/**
 * Sesiones persistentes del simulador interactivo.
 *
 * - useSimulatorSessions(clientSlug) → lista (summary, sin turns)
 * - useSimulatorSession(id) → detalle (incluye turns)
 * - useCreateSimulatorSession / useUpdateSimulatorSession / useDeleteSimulatorSession
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiGet, apiPatch, apiPost } from "@/lib/api";
import type { ClientSlug } from "@/types";

export interface SimulatorSessionSummary {
  id: string;
  clientSlug: ClientSlug;
  title: string;
  platform: string;
  kind: string;
  turnsCount: number;
  createdBy: string;
  createdByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SimulatorSession {
  id: string;
  clientSlug: ClientSlug;
  title: string;
  platform: string;
  kind: string;
  turns: unknown[];
  createdBy: string;
  createdByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useSimulatorSessions(clientSlug: ClientSlug | undefined) {
  return useQuery<SimulatorSessionSummary[]>({
    queryKey: ["simulator-sessions", clientSlug],
    enabled: !!clientSlug,
    queryFn: async () => {
      if (!clientSlug) return [];
      const qs = new URLSearchParams({ clientSlug });
      const res = await apiGet<{ items: SimulatorSessionSummary[] }>(
        `/simulator-sessions?${qs.toString()}`,
      );
      return res.items;
    },
  });
}

export function useSimulatorSession(id: string | null) {
  return useQuery<SimulatorSession | null>({
    queryKey: ["simulator-session", id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null;
      const res = await apiGet<{ session: SimulatorSession }>(
        `/simulator-sessions/${id}`,
      );
      return res.session;
    },
  });
}

interface CreateInput {
  clientSlug: ClientSlug;
  title: string;
  platform: string;
  kind: string;
  turns: unknown[];
}

export function useCreateSimulatorSession() {
  const qc = useQueryClient();
  return useMutation<SimulatorSession, Error, CreateInput>({
    mutationFn: async (input) => {
      const res = await apiPost<{ ok: boolean; session: SimulatorSession }>(
        "/simulator-sessions",
        input,
      );
      return res.session;
    },
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["simulator-sessions", s.clientSlug] });
    },
    onError: (err) => toast.error(`No se pudo guardar: ${err.message}`),
  });
}

export function useUpdateSimulatorSession() {
  const qc = useQueryClient();
  return useMutation<
    SimulatorSession,
    Error,
    { id: string; turns?: unknown[]; title?: string }
  >({
    mutationFn: async ({ id, ...patch }) => {
      const res = await apiPatch<{ ok: boolean; session: SimulatorSession }>(
        `/simulator-sessions/${id}`,
        patch,
      );
      return res.session;
    },
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["simulator-sessions", s.clientSlug] });
      qc.invalidateQueries({ queryKey: ["simulator-session", s.id] });
    },
    onError: (err) => toast.error(`No se pudo guardar: ${err.message}`),
  });
}

export function useDeleteSimulatorSession() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string; clientSlug: ClientSlug }>({
    mutationFn: async ({ id }) => {
      await api<{ ok: boolean }>(`/simulator-sessions/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["simulator-sessions", vars.clientSlug] });
      toast.success("Sesión eliminada");
    },
    onError: (err) => toast.error(err.message),
  });
}
